import { NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AgrConnectFormDirective, AgrNumberDirective } from '@next/core-lib/form';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';

import { Barn } from '../../models/setting.model';
import * as settingActions from '../../store/actions/setting.actions';
import { getSelectedBarn } from '../../store/selectors/setting.selector';
import { barnNameValidator } from '../../validators/barn-name-validator';

@Component({
  selector: 'app-barn-item',
  templateUrl: 'barn-item.component.html',
  imports: [
    ReactiveFormsModule,
    AgrConnectFormDirective,
    MatFormField,
    MatInput,
    NgIf,
    MatError,
    AgrNumberDirective,
    MatSuffix,
    MatButton,
    TranslatePipe,
  ],
})
export class BarnItemComponent {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);

  barn$: Observable<Barn> = this.store.select(getSelectedBarn);
  barnForm: FormGroup = new FormGroup({
    name: new FormControl(null, Validators.required, barnNameValidator(this.store)),
    surfaceArea: new FormControl(null, [Validators.required]),
    animalGroupBarnIdsInput: new FormControl(null),
  });

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.barn$
      .pipe(
        skipWhile((barn) => barn.id === undefined),
        take(1),
      )
      .subscribe((barn: Barn) => {
        this.barnForm = new FormGroup({
          name: new FormControl(null, Validators.required, barnNameValidator(this.store)),
          surfaceArea: new FormControl(null, [Validators.required]),
          animalGroupBarnIdsInput: new FormControl(barn.animalGroupBarnIds?.join(', ')),
        });
      });
  }

  cancel = () => this.router.navigate(['/settings/barn']);

  save() {
    if (this.barnForm.valid) {
      this.barn$.pipe(take(1)).subscribe((barn) => {
        // split the input value by commas and spaces
        let animalGroupBarnIdsSplit: string[] = [];
        if (this.barnForm.value.animalGroupBarnIdsInput) {
          animalGroupBarnIdsSplit = this.barnForm.value.animalGroupBarnIdsInput.split(/[,\s;]/);
        }

        const animalGroupBarnIds: string[] = [];

        for (const animalGroupBarnId of animalGroupBarnIdsSplit) {
          if (animalGroupBarnId !== '') {
            animalGroupBarnIds.push(animalGroupBarnId);
          }
        }

        this.store.dispatch(
          settingActions.upsertBarn({
            barn: {
              ...this.barnForm.value,
              id: barn ? barn.id : undefined,
              animalGroupBarnIds,
              isActive: true,
            },
          }),
        );
      });
    }
  }
}
